import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import { AddRounded, SearchRounded } from "@mui/icons-material";
import MainLayout from "../../layouts/main/main_layout";
import MerchantAdminLayout from "../../layouts/merchant_admin/merchant_admin_layout";
import Loading from "../../components/loading/loading";
import AccessDenied from "../../components/access_denied/access_denied";
import useUser from "../../providers/user/user_provider";
import { useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { getMerchantList, getVoucherList } from "../../services/merchant/merchant_service";
import MerchantsTable from "./components/merchants_table/merchants_table";
import VouchersTable from "./components/vouchers_table/vouchers_table";
import CreateVoucherDialog from "./components/create_voucher_dialog/create_voucher_dialog";
import CreateMerchantDialog from "./components/create_merchant_dialog/create_merchant_dialog";
import AssignVouchersDialog from "./components/assign_vouchers_dialog/assign_vouchers_dialog";
import RedeemVoucherDialog from "./components/redeem_voucher_dialog/redeem_voucher_dialog";
import AssignVouchersToUserDialog from "./components/assign_vouchers_to_user_dialog/assign_vouchers_to_user_dialog";
import FilterMerchants, {
  defaultFilters as defaultMerchantFilters,
  MerchantFiltersState,
} from "./components/filter_merchants/filter_merchants";
import SortMerchantsPopup, {
  DEFAULT_MERCHANTS_SORT_OPTION,
  MerchantsSortOption,
  merchantsSortOptionLabel,
  merchantsSortOptionToParams,
} from "./components/sort_merchants_popup/sort_merchants_popup";
import SortVouchersPopup, {
  DEFAULT_VOUCHERS_SORT_OPTION,
  VouchersSortOption,
  vouchersSortOptionLabel,
  vouchersSortOptionToParams,
} from "./components/sort_vouchers_popup/sort_vouchers_popup";
import { Merchant as MerchantType } from "./model/merchant_model";
import { ToolbarButton } from "../../components/toolbar_button/toolbar_button";
import FilterIcon from "../../assets/filter_icon.svg";
import SortIcon from "../../assets/sort.svg";
import OrgSelector from "../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import { black, greyEnabled, purple } from "../../theme/colors";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function Merchant() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(
    searchParams.has("currentTab") ? +searchParams?.get("currentTab")! : 0
  );
  const [merchantPage, setMerchantPage] = useState(
    searchParams.has("merchantPage") ? +searchParams?.get("merchantPage")! : 0
  );
  const [voucherPage, setVoucherPage] = useState(
    searchParams.has("voucherPage") ? +searchParams?.get("voucherPage")! : 0
  );
  const [merchantPageSize, setMerchantPageSize] = useState(8);
  const [voucherPageSize, setVoucherPageSize] = useState(8);
  const [merchantSearchValue, setMerchantSearchValue] = useState("");
  const [voucherSearchValue, setVoucherSearchValue] = useState("");
  const [merchantFilters, setMerchantFilters] = useState<MerchantFiltersState>(defaultMerchantFilters);
  const [merchantFilterModalOpen, setMerchantFilterModalOpen] = useState(false);
  const [voucherFilters, setVoucherFilters] = useState<MerchantFiltersState>(defaultMerchantFilters);
  const [voucherFilterModalOpen, setVoucherFilterModalOpen] = useState(false);
  const [merchantSortOption, setMerchantSortOption] = useState<MerchantsSortOption>(DEFAULT_MERCHANTS_SORT_OPTION);
  const [merchantSortDialogOpen, setMerchantSortDialogOpen] = useState(false);
  const [voucherSortOption, setVoucherSortOption] = useState<VouchersSortOption>(DEFAULT_VOUCHERS_SORT_OPTION);
  const [voucherSortDialogOpen, setVoucherSortDialogOpen] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  // Dialog states
  const [createVoucherDialogOpen, setCreateVoucherDialogOpen] = useState(false);
  const [createMerchantDialogOpen, setCreateMerchantDialogOpen] = useState(false);
  const [assignVouchersDialogOpen, setAssignVouchersDialogOpen] = useState(false);
  const [redeemVoucherDialogOpen, setRedeemVoucherDialogOpen] = useState(false);
  const [assignVouchersToUserDialogOpen, setAssignVouchersToUserDialogOpen] = useState(false);
  const [selectedMerchantForAssign, setSelectedMerchantForAssign] = useState<MerchantType | null>(null);

  const { hasPermission } = useUser();
  const queryClient = useQueryClient();

  const merchantFilterCount = [!!merchantFilters.dateFrom && !!merchantFilters.dateTo].filter(Boolean).length;
  const voucherFilterCount = [!!voucherFilters.dateFrom && !!voucherFilters.dateTo].filter(Boolean).length;

  useEffect(() => {
    setMerchantPage(0);
  }, [merchantSearchValue, merchantFilters, merchantSortOption, merchantPageSize, selectedOrgId]);

  useEffect(() => {
    setVoucherPage(0);
  }, [voucherSearchValue, voucherFilters, voucherSortOption, voucherPageSize, selectedOrgId]);

  // Merchants query
  const { data: merchantsData, isFetching: isMerchantsFetching } = useQuery(
    [
      "merchants",
      merchantPage,
      merchantPageSize,
      merchantSearchValue,
      merchantSortOption,
      merchantFilters.dateFrom,
      merchantFilters.dateTo,
      selectedOrgId,
    ],
    async () => {
      const { sortBy, sortOrder } = merchantsSortOptionToParams(merchantSortOption);
      const response = await getMerchantList({
        page: merchantPage + 1,
        size: merchantPageSize,
        keyword: merchantSearchValue.trim() || undefined,
        sortBy,
        sortOrder,
        fromDate: merchantFilters.dateFrom ? new Date(merchantFilters.dateFrom).toISOString().split('T')[0] : undefined,
        toDate: merchantFilters.dateTo ? new Date(merchantFilters.dateTo).toISOString().split('T')[0] : undefined,
        orgId: selectedOrgId,
      });
      const raw = response?.data?.data?.data?.list;
      return { list: raw ?? [], total: response?.data?.data?.data?.total ?? 0 };
    },
    {
      enabled: true, // Always load merchants for redeem voucher dialog
      refetchOnWindowFocus: false,
    }
  );

  // Vouchers query
  const { data: vouchersData, isFetching: isVouchersFetching } = useQuery(
    [
      "vouchers",
      voucherPage,
      voucherPageSize,
      voucherSearchValue,
      voucherSortOption,
      voucherFilters.dateFrom,
      voucherFilters.dateTo,
      selectedOrgId,
    ],
    async () => {
      const { sortBy, sortOrder } = vouchersSortOptionToParams(voucherSortOption);
      const response = await getVoucherList({
        page: voucherPage + 1,
        size: voucherPageSize,
        keyword: voucherSearchValue.trim() || undefined,
        sortBy,
        sortOrder,
        dateFrom: voucherFilters.dateFrom ? new Date(voucherFilters.dateFrom).toISOString().split('T')[0] : undefined,
        dateTo: voucherFilters.dateTo ? new Date(voucherFilters.dateTo).toISOString().split('T')[0] : undefined,
        orgId: selectedOrgId,
      });
      return response?.data?.data ?? { list: [], total: 0 };
    },
    {
      enabled: currentTab === 1,
      refetchOnWindowFocus: false,
    }
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSearchParams({ currentTab: newValue.toString() }, { replace: true });
  };

  useEffect(() => {
    setSearchParams(
      {
        currentTab: currentTab.toString(),
        merchantPage: merchantPage.toString(),
        voucherPage: voucherPage.toString(),
      },
      { replace: true }
    );
  }, [currentTab, merchantPage, voucherPage, setSearchParams]);

  const handleDialogSuccess = () => {
    queryClient.invalidateQueries(["merchants"]);
    queryClient.invalidateQueries(["vouchers"]);
  };

  function toolbarRow({
    searchValue,
    setSearchValue,
    placeholder,
    sortLabel,
    onOpenSort,
    filterCount,
    onOpenFilters,
    actions,
  }: {
    searchValue: string;
    setSearchValue: (val: string) => void;
    placeholder: string;
    sortLabel: string;
    onOpenSort: () => void;
    filterCount: number;
    onOpenFilters: () => void;
    actions?: React.ReactNode;
  }) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        p={2}
      >
        <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
          <TextField
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            size="small"
            fullWidth
            placeholder={placeholder}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <OrgSelector lockToOwnOrg />
          <ToolbarButton icon={SortIcon} label={sortLabel} onClick={onOpenSort} />
          <ToolbarButton
            icon={FilterIcon}
            label="Filters"
            badgeCount={filterCount}
            onClick={onOpenFilters}
          />
          {actions}
        </Box>
      </Box>
    );
  }

  function renderComponentBasedOnTabIndex() {
    switch (currentTab) {
      case 0:
        return (
          <>
            {toolbarRow({
              searchValue: merchantSearchValue,
              setSearchValue: setMerchantSearchValue,
              placeholder: "Search by mobile number, merchant name or pincode",
              sortLabel: merchantsSortOptionLabel(merchantSortOption),
              onOpenSort: () => setMerchantSortDialogOpen(true),
              filterCount: merchantFilterCount,
              onOpenFilters: () => setMerchantFilterModalOpen(true),
              actions: hasPermission("merchants", "create") && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                  onClick={() => setCreateMerchantDialogOpen(true)}
                  sx={{
                    bgcolor: purple,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#7A2074" },
                  }}
                >
                  Add Merchant
                </Button>
              ),
            })}
            {isMerchantsFetching ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Loading />
              </Box>
            ) : (
              <MerchantsTable
                data={merchantsData?.list || []}
                count={merchantsData?.total || 0}
                page={merchantPage}
                setPage={setMerchantPage}
                pageSize={merchantPageSize}
                setPageSize={setMerchantPageSize}
                onAssignVouchers={(merchant) => {
                  setSelectedMerchantForAssign(merchant);
                  setAssignVouchersDialogOpen(true);
                }}
              />
            )}
          </>
        );
      case 1:
        return (
          <>
            {toolbarRow({
              searchValue: voucherSearchValue,
              setSearchValue: setVoucherSearchValue,
              placeholder: "Search by voucher code, merchant name or user mobile number",
              sortLabel: vouchersSortOptionLabel(voucherSortOption),
              onOpenSort: () => setVoucherSortDialogOpen(true),
              filterCount: voucherFilterCount,
              onOpenFilters: () => setVoucherFilterModalOpen(true),
              actions: (
                <Box display="flex" gap={1.5}>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                    onClick={() => setCreateVoucherDialogOpen(true)}
                    sx={{
                      bgcolor: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#7A2074" },
                    }}
                  >
                    Create Vouchers
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setRedeemVoucherDialogOpen(true)}
                    sx={{
                      borderColor: purple,
                      color: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Redeem Voucher
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setAssignVouchersToUserDialogOpen(true)}
                    sx={{
                      borderColor: purple,
                      color: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Assign to User
                  </Button>
                </Box>
              ),
            })}
            {isVouchersFetching ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Loading />
              </Box>
            ) : (
              <VouchersTable
                data={vouchersData?.list || []}
                count={vouchersData?.total || 0}
                page={voucherPage}
                setPage={setVoucherPage}
                pageSize={voucherPageSize}
                setPageSize={setVoucherPageSize}
              />
            )}
          </>
        );
      default:
        return null;
    }
  }

  return (
    <MainLayout>
      {hasPermission("merchants", "read") ? (
        <MerchantAdminLayout>
          <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 1 }}>
            <Tabs
              value={currentTab}
              aria-label="merchant tabs"
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { backgroundColor: purple, height: 3, borderRadius: 3 } }}
            >
              <Tab label="Merchants" sx={{ textTransform: "capitalize", color: black }} />
              <Tab label="Vouchers" sx={{ textTransform: "capitalize", color: black }} />
            </Tabs>
          </Box>
          {renderComponentBasedOnTabIndex()}
        </MerchantAdminLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100vh">
          <AccessDenied />
        </Box>
      )}

      {/* Dialogs */}
      <CreateVoucherDialog
        open={createVoucherDialogOpen}
        onClose={() => setCreateVoucherDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />

      <CreateMerchantDialog
        open={createMerchantDialogOpen}
        onClose={() => setCreateMerchantDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />

      <AssignVouchersDialog
        open={assignVouchersDialogOpen}
        onClose={() => setAssignVouchersDialogOpen(false)}
        onSuccess={handleDialogSuccess}
        selectedMerchant={selectedMerchantForAssign}
      />

      <RedeemVoucherDialog
        open={redeemVoucherDialogOpen}
        onClose={() => setRedeemVoucherDialogOpen(false)}
        onSuccess={handleDialogSuccess}
        merchants={merchantsData?.list || []}
      />

      <AssignVouchersToUserDialog
        open={assignVouchersToUserDialogOpen}
        onClose={() => setAssignVouchersToUserDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />

      <FilterMerchants
        openModal={merchantFilterModalOpen}
        setOpenModal={setMerchantFilterModalOpen}
        resetFilter={() => setMerchantFilters(defaultMerchantFilters)}
        filters={merchantFilters}
        onApplyFilters={setMerchantFilters}
        title="Filter Merchants"
      />

      <FilterMerchants
        openModal={voucherFilterModalOpen}
        setOpenModal={setVoucherFilterModalOpen}
        resetFilter={() => setVoucherFilters(defaultMerchantFilters)}
        filters={voucherFilters}
        onApplyFilters={setVoucherFilters}
        title="Filter Vouchers"
      />

      <SortMerchantsPopup
        open={merchantSortDialogOpen}
        onClose={() => setMerchantSortDialogOpen(false)}
        sortOption={merchantSortOption}
        setSortOption={setMerchantSortOption}
      />

      <SortVouchersPopup
        open={voucherSortDialogOpen}
        onClose={() => setVoucherSortDialogOpen(false)}
        sortOption={voucherSortOption}
        setSortOption={setVoucherSortOption}
      />
    </MainLayout>
  );
}
